/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Reports", description = "Create recurring reports that run on a schedule and deliver results via email or Slack. Powered by your warehouse data and connected marketing channels.", lastUpdatedDate = new Date(1779753600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Reports",
        description: "Create recurring reports that run on a schedule and deliver results via email or Slack. Powered by your warehouse data and connected marketing channels.",
        lastUpdatedDate: "Tue May 26 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Create a report",
          "depth": 0,
          "id": "create-a-report"
        }, {
          "value": "Review and edit instructions",
          "depth": 0,
          "id": "review-and-edit-instructions"
        }, {
          "value": "Schedule and deliver reports",
          "depth": 0,
          "id": "schedule-and-deliver-reports"
        }, {
          "value": "View report results",
          "depth": 0,
          "id": "view-report-results"
        }, {
          "value": "Review and improve report instructions",
          "depth": 1,
          "id": "review-and-improve-report-instructions"
        }, {
          "value": "Share reports",
          "depth": 1,
          "id": "share-reports"
        }, {
          "value": "Attach a PDF version",
          "depth": 1,
          "id": "attach-a-pdf-version"
        }, {
          "value": "Use custom agents in reports",
          "depth": 0,
          "id": "use-custom-agents-in-reports"
        }, {
          "value": "Manage reports",
          "depth": 0,
          "id": "manage-reports"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      ol: "ol",
      li: "li",
      ul: "ul",
      h3: "h3",
      a: "a"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Reports are saved prompts that run on a schedule and deliver results automatically. Turn any one-off chat analysis into a recurring report delivered via email or Slack."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Reports are saved prompts that run on a schedule. You create a report once and it runs automatically, delivering results to your inbox or a Slack channel."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Reports are viewable by all users in your workspace. Any user with access to Agents can see report results, regardless of who created the report."
        })
      }), "\n", _jsx(_components.p, {
        children: "Each report is built from a prompt that describes what the report should cover. You can write the prompt yourself, start from a template, or convert an existing chat conversation into a report."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/reports-list.png",
          alt: "Reports list"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "create-a-report",
        children: "Create a report"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a report, go to ", _jsx(_components.strong, {
          children: "Agents > Reports"
        }), " and click ", _jsx(_components.strong, {
          children: "Create report"
        }), ". The creation dialog walks you through four steps:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Select a parent model"
            }), " — choose the parent model the report should query. This determines which customer data is available to the report."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Select channels"
            }), " (optional) — choose which connected ad platforms or marketing channels the report should include. This adds campaign performance data from platforms like Meta, Google Ads, and others."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Set a time period"
            }), " — choose the time window the report should cover. Options include ", _jsx(_components.strong, {
              children: "Last 7 days"
            }), ", ", _jsx(_components.strong, {
              children: "Last 30 days"
            }), ", ", _jsx(_components.strong, {
              children: "Last quarter"
            }), ", or a ", _jsx(_components.strong, {
              children: "Custom"
            }), " range."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Write a description"
            }), " — describe what the report should cover. You can write your own prompt or start from a template. Available templates include:"]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Performance summary"
              }), " — overall campaign and channel performance metrics"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Channel comparison"
              }), " — side-by-side performance across marketing channels"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Campaign analysis"
              }), " — deep dive into specific campaign results"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Audience insights"
              }), " — customer segment breakdowns and behavioral patterns"]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["After filling in the description, click ", _jsx(_components.strong, {
          children: "Generate instructions"
        }), " to have the agent create a detailed report prompt based on your inputs. You can also click ", _jsx(_components.strong, {
          children: "Skip instructions"
        }), " to use your description as-is."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/create-report.png",
          alt: "Create report"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Build reports from chat."
          }), " After any analysis in a chat, ask the agent: \"Convert this into a reusable report prompt I can run weekly. Make all time references relative and format the output for team distribution.\" This turns a one-off analysis into a scheduled report without starting from scratch."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "review-and-edit-instructions",
        children: "Review and edit instructions"
      }), "\n", _jsx(_components.p, {
        children: "Before a report runs, you can review and edit the generated instructions. The instructions are the prompt the agent uses to generate the report. They control the metrics, formatting, time ranges, and output structure."
      }), "\n", _jsxs(_components.p, {
        children: ["Editing instructions lets you fine-tune the report without recreating it. For example, you might adjust which metrics are included, change the sort order, or add a specific comparison. You can edit the instructions directly or use the ", _jsx(_components.strong, {
          children: "Describe changes to instructions"
        }), " chat input at the bottom of the instructions panel to update them conversationally."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/improve-instructions.png",
          alt: "Edit report instructions"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "schedule-and-deliver-reports",
        children: "Schedule and deliver reports"
      }), "\n", _jsxs(_components.p, {
        children: ["Each report has a ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab where you set the schedule and delivery method."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Schedule type"
        }), " — choose how the report runs:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Manual"
          }), " — trigger the report manually in-app by clicking ", _jsx(_components.strong, {
            children: "Generate report"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom recurrence"
          }), " — set a recurring schedule. Configure the time (for example, 5:00 AM), the days of the week, and optionally add multiple recurrences."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Delivery method"
        }), " (optional) — choose where completed reports are delivered:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Email"
          }), " — the report is delivered to one or more email addresses"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Slack"
          }), " — the report is posted to a Slack channel"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/configuration.png",
          alt: "Report configuration with schedule and delivery options"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "view-report-results",
        children: "View report results"
      }), "\n", _jsxs(_components.p, {
        children: ["Each report has a ", _jsx(_components.strong, {
          children: "Reports"
        }), " tab that shows the history of past runs. Each run displays its status (for example, Completed), name, date, and delivery method."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/view-report-example.png",
          alt: "Report view"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["From any report result, you can click ", _jsx(_components.strong, {
          children: "Chat with this report"
        }), " to ask follow-up questions about the data. This opens a new chat with the report context preloaded, so you can dig deeper without recreating the analysis."]
      }), "\n", _jsx(_components.h3, {
        id: "review-and-improve-report-instructions",
        children: "Review and improve report instructions"
      }), "\n", _jsxs(_components.p, {
        children: ["When viewing a report result, you can switch to the ", _jsx(_components.strong, {
          children: "Improve instructions"
        }), " tab to enter review mode. In review mode, you can click any section of the report to leave specific feedback, or use the input at the bottom for overall changes. Your comments are used to update the report instructions, so the next run reflects your changes."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/agents/reports/review-mode-panel.png",
          alt: "Review mode showing the Improve instructions tab and feedback panel"
        })
      }), "\n", _jsx(_components.h3, {
        id: "share-reports",
        children: "Share reports"
      }), "\n", _jsxs(_components.p, {
        children: ["To share a report result with workspace members, click the ", _jsx(_components.strong, {
          children: "share icon"
        }), " at the top of the report. This copies a link to your clipboard that any workspace member with access to Agents can open."]
      }), "\n", _jsx(_components.h3, {
        id: "attach-a-pdf-version",
        children: "Attach a PDF version"
      }), "\n", _jsx(_components.p, {
        children: "You can include a PDF copy of the report as an email attachment when using email delivery."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the report and go to the ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Delivery method"
          }), ", select ", _jsx(_components.strong, {
            children: "Email"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Check ", _jsx(_components.strong, {
            children: "Attach a PDF version"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When this is enabled, each email delivery includes the report output as a PDF attachment alongside the email body."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/agents/reports/attach-pdf-version.png",
          alt: "Attach a PDF version checkbox under email delivery settings"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-custom-agents-in-reports",
        children: "Use custom agents in reports"
      }), "\n", _jsx(_components.p, {
        children: "Reports can use custom agents to apply specific instructions, metric definitions, or formatting rules. When creating a report, you can select a custom agent to scope the report to a particular domain or team."
      }), "\n", _jsx(_components.p, {
        children: "For example, a \"Weekly Creative Reporting\" custom agent might include instructions for analyzing creative fatigue, formatting results by campaign objective, and flagging underperforming ads. Any report that uses this custom agent follows those instructions automatically."
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/agents/custom-agents",
          children: "Custom agents"
        }), " for how to create and configure custom agents."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/custom-agents/custom-agent-report.png",
          alt: "Create report with custom agent"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "manage-reports",
        children: "Manage reports"
      }), "\n", _jsx(_components.p, {
        children: "The Reports list shows all reports in your workspace with the following columns:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Name"
          }), " — the report title"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Frequency"
          }), " — the configured schedule (for example, \"Every day at 5:00 AM\") or \"--\" for on-demand reports"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Reports"
          }), " — the number of times the report has run"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delivery"
          }), " — the configured delivery method (email, Slack, or \"--\" if not set)"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Last updated"
          }), " — when the report was last modified or run"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Click the ", _jsx(_components.strong, {
          children: "..."
        }), " menu on any report to edit, duplicate, or delete it."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "agents/reports/edit-report.png",
          alt: "Edit report"
        })
      })]
    });
  }
}
export default MDXContent;
